import React, { useEffect, useMemo, useState } from 'react';

import OnboardingLayout from '../components/OnboardingLayout/OnboardingLayout';
import StepHeader from '../components/StepHeader/StepHeader';
import { Button } from '../components/Button/Button';
import { ChoiceGrid, ChoiceCard, ExpandToggle } from '../components/ChoiceCard/ChoiceCard';
import {
    NICHES,
    NICHE_PRIMARY_COUNT,
    getNiche,
    localized,
    loadNicheOptionIds,
    nicheOptionIdsWithin,
    searchNiches,
} from '../onboardingV2.niches';
import { normalizeSpaces } from '../../../utils/dbText';
import { Search, NoResults, CustomNiche } from './NicheStep.styles';

const CUSTOM_MAX = 60;
const OTHER_KEY = 'otro';
const sameText = (a, b) =>
    String(a || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase() ===
    String(b || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

/**
 * Step 2 · nicho ("¿Qué vendes?"): one niche from the backend onboarding question 1. Saved for stats
 * (Mixpanel + `on_boarding_options` at signup) and it drives the suggested categories of the next step
 * and the product examples.
 */
const NicheStep = ({ ctx }) => {
    const { t, isDemo, api, language, onboarding, updateOnboarding, analytics, goTo, trackStep } = ctx;
    // A tap is kept even if the merchant goes back without continuing.
    const [selected, setSelected] = useState(
        (onboarding.niche && onboarding.niche.key) || onboarding.nicheSelection || null
    );
    // A niche that isn't in the list ("Autos"): saved as "Otro" plus the merchant's own name for it.
    const [custom, setCustom] = useState(
        () => (onboarding.niche && onboarding.niche.custom) || onboarding.nicheCustom || null
    );
    const [continuing, setContinuing] = useState(false);
    const [query, setQuery] = useState('');
    const [expanded, setExpanded] = useState(() => {
        const index = NICHES.findIndex((niche) => niche.key === selected);
        return index >= NICHE_PRIMARY_COUNT;
    });
    const [optionIds, setOptionIds] = useState({});

    // Backend ids of this environment, matched by order. Without them the niche still goes to Mixpanel.
    useEffect(() => {
        let active = true;
        loadNicheOptionIds(api).then((ids) => {
            if (active) setOptionIds(ids);
        });
        return () => {
            active = false;
        };
    }, [api]);

    const choose = (key) => {
        setSelected(key);
        setCustom(null);
        updateOnboarding({ nicheSelection: key, nicheCustom: null });
    };

    const chooseCustom = (text) => {
        const label = normalizeSpaces(text).slice(0, CUSTOM_MAX);
        if (!label) return;
        setSelected(OTHER_KEY);
        setCustom(label);
        updateOnboarding({ nicheSelection: OTHER_KEY, nicheCustom: label });
    };

    const q = query.trim();
    const results = useMemo(() => {
        const found = searchNiches(q, language);
        return q || expanded ? found : found.slice(0, NICHE_PRIMARY_COUNT);
    }, [q, expanded, language]);
    // Offer the typed text as the niche unless it is exactly one of the list.
    const canUseCustom = !!q && !results.some((niche) => sameText(localized(niche.label, language), q));

    // What merchants look for, and searches with no niche (missing niches). Once the typing stops.
    useEffect(() => {
        if (!q) return undefined;
        const timer = window.setTimeout(() => {
            analytics.track('onboarding_niche_searched', {
                query: q.slice(0, 60),
                results_count: results.length,
                results: results.slice(0, 5).map((niche) => niche.key).join(','),
            });
        }, 1200);
        return () => window.clearTimeout(timer);
        // Only when the query changes.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [q]);

    const onContinue = async () => {
        const niche = getNiche(selected);
        if (!niche || continuing) return;
        setContinuing(true);
        // A quick tap can beat the request: wait for it a little before saving without the id.
        const ids = Object.keys(optionIds).length ? optionIds : await nicheOptionIdsWithin(api, 3000);
        const optionId = ids[niche.order] || null;
        const nicheCustom = niche.key === OTHER_KEY ? custom : null;
        updateOnboarding({
            niche: { key: niche.key, order: niche.order, optionId, custom: nicheCustom },
            // Product examples (name / price placeholders and tip) follow the niche.
            category: niche.productKey,
        });
        analytics.register({ niche: niche.key, niche_custom: nicheCustom });
        // The step's result goes on its signup_step_completed (one event per step).
        trackStep('signup_step_completed', {
            niche: niche.key,
            niche_label: localized(niche.label, 'es'),
            niche_order: niche.order,
            niche_option_id: optionId,
            niche_custom: nicheCustom,
            searched: !!q,
            search_query: q || null,
            expanded_more: expanded,
            changed_from: onboarding.niche && onboarding.niche.key !== niche.key ? onboarding.niche.key : null,
        });
        goTo('tienda');
    };

    const onBack = () => {
        ctx.noteBack();
        goTo('cuenta');
    };

    return (
        <OnboardingLayout
            preview={ctx.preview}
            step={ctx.stepNumberOf('nicho')}
            total={ctx.totalSteps}
            stepKey="nicho"
            title={t('SignUpV2.nicho.title')}
            headingId="nicho-title"
            isDemo={isDemo}
            onBack={onBack}
            footer={
                <Button onClick={onContinue} iconRight="ri-arrow-right-line" disabled={!selected || continuing}>
                    {t('SignUpV2.nicho.cta')}
                </Button>
            }
        >
            <StepHeader id="nicho-title" center title={t('SignUpV2.nicho.title')} subtitle={t('SignUpV2.nicho.subtitle')} />

            <Search>
                <i className="ri-search-line" aria-hidden="true" />
                <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder={t('SignUpV2.nicho.searchPlaceholder')}
                    data-field="niche_search"
                    aria-label={t('SignUpV2.nicho.searchPlaceholder')}
                />
            </Search>

            {results.length === 0 && <NoResults>{t('SignUpV2.nicho.noResults')}</NoResults>}
            {results.length > 0 && (
                <ChoiceGrid labelledBy="nicho-title" id="niche-grid">
                    {results.map((niche) => (
                        <ChoiceCard
                            key={niche.key}
                            icon={niche.icon}
                            label={localized(niche.label, language)}
                            selected={selected === niche.key}
                            onSelect={() => choose(niche.key)}
                        />
                    ))}
                </ChoiceGrid>
            )}
            {canUseCustom && (
                <CustomNiche>
                    <ChoiceCard
                        icon="ri-add-line"
                        label={t('SignUpV2.nicho.useCustom', { query: normalizeSpaces(q).slice(0, CUSTOM_MAX) })}
                        selected={selected === OTHER_KEY && sameText(custom, q)}
                        onSelect={() => chooseCustom(q)}
                    />
                </CustomNiche>
            )}
            {!q && selected === OTHER_KEY && custom && (
                <NoResults>{t('SignUpV2.nicho.customSelected', { query: custom })}</NoResults>
            )}

            {!q && !expanded && (
                <ExpandToggle
                    expanded={expanded}
                    controls="niche-grid"
                    label={t('SignUpV2.nicho.more')}
                    onToggle={() => {
                        analytics.track('onboarding_niches_expanded', { selected_before: selected });
                        setExpanded(true);
                    }}
                />
            )}
        </OnboardingLayout>
    );
};

export default NicheStep;
